<template>
  <profile-edit-section-card
    v-if="localState"
    title="Experience"
    breadcrumb-label="Profile"
    xl-card
    :saving="isSaving"
    :has-unsaved-changes="hasUnsavedChanges"
    :has-invalid-input="hasInvalidInput"
    @breadcrumb-click="cancelEdit"
    @save="handleSave"
    @cancel="cancelEdit"
  >
    <div>
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-16 pb-24">
        <div class="min-w-0 flex-1">
          <p class="font-bold">Paid professional experience</p>
          <p class="text-small text-grey-text-subdued mt-4">Babysitter, nanny, teacher, camp counselor, or tutor.</p>
        </div>
        <poppins-counter v-model="yearsExperience" label="years" :min="0" :max="50" />
      </div>

      <horizontal-divider />

      <div class="flex flex-col md:flex-row md:items-center justify-between gap-16 py-24">
        <div class="min-w-0 flex-1">
          <p class="font-bold">Informal/family experience</p>
          <p class="text-small text-grey-text-subdued mt-4">Relatives, siblings, or neighbor help.</p>
        </div>
        <poppins-counter v-model="informalYearsExperience" label="years" :min="0" :max="50" />
      </div>
    </div>

    <horizontal-divider />

    <fieldset>
      <legend class="form__label pt-24 pb-8">Experience with ages</legend>
      <p class="text-caption mb-16">Select all that apply</p>
      <toggle-token-group
        custom-id="age-group-tokens"
        :tokens="ageGroupTokens"
        :max-columns="3"
        :status="ageGroupsStatus"
        :helper-text="ageGroupsHelperText"
        legend="What ages do you have experience with?"
        @update:model-value="updateAgeGroupTokens"
      />
    </fieldset>
  </profile-edit-section-card>
</template>

<script lang="ts">
// Components
import ProfileEditSectionCard from '../../components/ProfileEditSectionCard.vue';
import ToggleTokenGroup from '@/components/toggle_token/ToggleTokenGroup.vue';
import PoppinsCounter from '@/components/counter/PoppinsCounter.vue';
import HorizontalDivider from '@/components/divider/HorizontalDivider.vue';

// Types and helpers
import type { BabysitterProfileFormState } from '../../__types__/BabysitterProfileTypes';
import { childCareAgeGroupsToggleTokenOptions } from '@/apps/sitter/signup/profile/components/__types__/AgeTypes';
import HelperStatus from '@/helpers/status';
import profileEditMixin from '../../mixins/profileEditMixin';

export default {
  name: 'ChildCareExperienceForm',
  components: {
    ProfileEditSectionCard,
    ToggleTokenGroup,
    PoppinsCounter,
    HorizontalDivider,
  },
  mixins: [profileEditMixin],
  data(): any {
    return {
      localState: null,
      originalState: null,
      hasInvalidInput: false,
      ageGroupsStatus: HelperStatus.Neutral,
    };
  },
  computed: {
    hasUnsavedChanges(): boolean {
      if (!this.localState || !this.originalState) return false;
      return JSON.stringify(this.localState) !== JSON.stringify(this.originalState);
    },
    yearsExperience: {
      get(): number {
        return this.localState?.yearsExperience ?? 0;
      },
      set(value: number) {
        if (this.localState) this.localState.yearsExperience = value;
      },
    },
    informalYearsExperience: {
      get(): number {
        return this.localState?.informalYearsExperience ?? 0;
      },
      set(value: number) {
        if (this.localState) this.localState.informalYearsExperience = value;
      },
    },
    ageGroupsHelperText(): string | null {
      if (this.ageGroupsStatus === HelperStatus.Error) {
        return 'Selecting at least one age is required.';
      }
      return null;
    },
    ageGroupTokens(): Object[] {
      const selectedAgeGroups = this.localState?.ageGroups || [];
      return childCareAgeGroupsToggleTokenOptions.map((option) => ({
        ...option,
        value: selectedAgeGroups.includes(option.type),
      }));
    },
  },
  watch: {
    $route() {
      this.initializeFormState();
    },
    formState: {
      handler(newState) {
        if (newState) {
          this.initializeFormState();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.initializeFormState();
  },
  methods: {
    initializeFormState() {
      const state = this.formState as BabysitterProfileFormState | null;
      if (!state) return;

      this.localState = {
        yearsExperience: state.yearsExperience ?? 0,
        informalYearsExperience: state.informalYearsExperience ?? 0,
        ageGroups: [...(state.ageGroups || [])],
      };
      this.originalState = JSON.parse(JSON.stringify(this.localState));
    },
    updateAgeGroupTokens(newTokens) {
      const newValue = [] as string[];
      newTokens.forEach((option) => {
        if (option.value) newValue.push(option.type);
      });
      if (this.ageGroupsStatus === HelperStatus.Error && newValue.length) {
        this.ageGroupsStatus = HelperStatus.Neutral;
        this.hasInvalidInput = false;
      }
      this.localState.ageGroups = newValue;
    },
    async handleSave() {
      if (!this.localState) return;

      const ageGroupsInvalid = !this.localState.ageGroups?.length;

      if (ageGroupsInvalid) {
        this.ageGroupsStatus = HelperStatus.Error;
        this.hasInvalidInput = true;
        this.$nextTick(() => {
          const element = document.getElementById('age-group-tokens-helper');
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        return;
      }
      if (this.hasUnsavedChanges) {
        await this.saveSection({
          yearsExperience: this.localState.yearsExperience,
          informalYearsExperience: this.localState.informalYearsExperience,
          ageGroups: [...this.localState.ageGroups],
        });
      }
    },
  },
};
</script>
